import React, { useState, useEffect, useCallback } from 'react';
import { fetchMedicationsByTab } from '../../services/medicationService';

const ControlledDrugsBookTab = ({ serverId }) => {
    const [medications, setMedications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const loadTabData = useCallback(async (isLoadMore = false) => {
        try {
            setIsLoading(true);
            const { medications: newMeds, lastVisible: newLastVisible } = await fetchMedicationsByTab(
                serverId, 
                'all', 
                isLoadMore ? lastVisible : null, 
                30,
                { isControlled: true } // Fetch only controlled drugs
            );
            
            if (isLoadMore) {
                setMedications(prev => [...prev, ...newMeds]);
            } else {
                setMedications(newMeds);
            }
            
            setLastVisible(newLastVisible);
            setHasMore(newMeds.length === 30);
        } catch (err) {
            console.error("Failed to load controlled drugs", err);
        } finally {
            setIsLoading(false);
        }
    }, [serverId, lastVisible]);

    useEffect(() => {
        loadTabData(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverId]);

    const filteredMedications = medications.filter(med => {
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const hay = `${med.medicineName || ''} ${med.userID || ''} ${med.assignedBy || ''}`.toLowerCase();
            return hay.includes(term);
        }
        return true;
    });

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center' }}>
                <div>
                    <h2 style={{ fontSize: '20px', color: '#2d3748', margin: '0 0 5px 0' }}>Controlled Drugs Book</h2>
                    <p style={{ color: '#718096', margin: 0, fontSize: '14px' }}>Ledger for all controlled substance assignments and inventory.</p>
                </div>
                
                <input 
                    type="text"
                    placeholder="Search ledger..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '250px' }}
                />
            </div>

            {isLoading && medications.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>Loading ledger...</div>
            ) : filteredMedications.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f7fafc', borderRadius: '8px', color: '#718096' }}>
                    No controlled drugs found in the ledger.
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                        <thead style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                            <tr>
                                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#4a5568', fontSize: '14px', fontWeight: '600' }}>Date</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#4a5568', fontSize: '14px', fontWeight: '600' }}>User ID</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#4a5568', fontSize: '14px', fontWeight: '600' }}>Medication Name</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#4a5568', fontSize: '14px', fontWeight: '600' }}>Route</th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', color: '#4a5568', fontSize: '14px', fontWeight: '600' }}>Approval Trail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredMedications.map((med, index) => {
                                const dateObj = med.createdAt?.seconds ? new Date(med.createdAt.seconds * 1000) : med.date?.seconds ? new Date(med.date.seconds * 1000) : new Date();
                                
                                return (
                                    <tr key={med.id || index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <td style={{ padding: '12px 16px', color: '#4a5568', fontSize: '14px' }}>
                                            {dateObj.toLocaleDateString()} {dateObj.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                        </td>
                                        <td style={{ padding: '12px 16px', color: '#2d3748', fontSize: '14px', fontWeight: '500' }}>
                                            {med.userID || 'Unknown'}
                                        </td>
                                        <td style={{ padding: '12px 16px', color: '#2d3748', fontSize: '14px', fontWeight: 'bold' }}>
                                            {med.medicineName || med.medicationName || 'Unknown'}
                                        </td>
                                        <td style={{ padding: '12px 16px', color: '#718096', fontSize: '14px' }}>
                                            {med.route || 'Oral'}
                                        </td>
                                        <td style={{ padding: '12px 16px', color: '#718096', fontSize: '13px' }}>
                                            {med.assignedBy ? `Assigned by: ${med.assignedBy}` : 'Unknown Assigner'}
                                            {med.doubleSignedBy && <div style={{ color: '#38a169' }}>Double signed by: {med.doubleSignedBy}</div>}
                                            {med.adminOverride && <div style={{ color: '#3182ce', fontWeight: 'bold' }}>Admin Override</div>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    
                    {hasMore && (
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <button 
                                onClick={() => loadTabData(true)}
                                disabled={isLoading}
                                style={{ padding: '10px 20px', backgroundColor: 'transparent', color: '#3182ce', border: '1px solid #3182ce', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                            >
                                {isLoading ? 'Loading...' : 'Load More Records'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ControlledDrugsBookTab;
